import { useAuth } from '@/services/auth';
import { useLogging } from '@/contexts/LoggingContext';
import logger from '@/utils/logger';

type LogLevel = 'all' | 'error' | 'warn' | 'info' | 'debug' | 'trace';

const LOG_LEVELS: LogLevel[] = ['all', 'error', 'warn', 'info', 'debug', 'trace'];

/**
 * Logging Configuration Page
 *
 * Allows developers to configure log filtering and enable backend log streaming.
 * Admin users can enable streaming to see backend logs in the browser console.
 */
export default function LoggingConfig() {
  const { isAdmin } = useAuth();
  const { config, isConnected, connectionError, updateConfig, reconnect } = useLogging();

  const handleLevelChange = (level: LogLevel) => {
    updateConfig({ level });
  };

  const handleCategoriesChange = (categories: string) => {
    updateConfig({ categories });
  };

  const handleStreamingToggle = () => {
    if (!isAdmin) {
      logger.warn('DevTools', 'Non-admin user attempted to enable streaming');
      return;
    }
    updateConfig({ streamingEnabled: !config.streamingEnabled });
  };

  const handleClearConsole = () => {
    console.clear();
    logger.info('DevTools', 'Console cleared');
  };

  return (
    <div className="p-8 overflow-auto">
      <h1 className="text-3xl font-bold mb-8">Logging Configuration</h1>

      {/* Filter Controls Section */}
      <div className="bg-surface-2 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filter Controls</h2>

        {/* Log Level Dropdown */}
        <div className="mb-4">
          <label htmlFor="log-level" className="block text-sm font-medium mb-2">
            Log Level
          </label>
          <select
            id="log-level"
            value={config.level}
            onChange={(e) => handleLevelChange(e.target.value as LogLevel)}
            className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {LOG_LEVELS.map(level => (
              <option key={level} value={level}>
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </option>
            ))}
          </select>
          <p className="text-sm text-muted mt-1">
            Show logs at this level and above
          </p>
        </div>

        {/* Category Filter Input */}
        <div className="mb-4">
          <label htmlFor="categories" className="block text-sm font-medium mb-2">
            Category Filters
          </label>
          <input
            id="categories"
            type="text"
            value={config.categories}
            onChange={(e) => handleCategoriesChange(e.target.value)}
            placeholder="e.g., API, UI, Auth, API.*"
            className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-sm text-muted mt-1">
            Comma-separated categories. Use * for wildcards (e.g., API.*, UI.modal.*)
          </p>
          <p className="text-sm text-muted mt-1">
            Common categories: API, UI, Auth, Upload, State, Navigation, Storage, Database
          </p>
        </div>

        {/* Clear Filters Button */}
        <button
          onClick={() => updateConfig({ level: 'info', categories: '' })}
          className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
        >
          Clear Filters
        </button>
      </div>

      {/* Streaming Controls Section */}
      <div className="bg-surface-2 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Backend Streaming</h2>

        {/* Streaming Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <label htmlFor="streaming-toggle" className="text-sm font-medium">
              Stream Backend Logs {!isAdmin && '(Admin Only)'}
            </label>
            <p className="text-sm text-muted mt-1">
              {isAdmin
                ? 'Show API, Database, and Storage logs in browser console'
                : 'Requires @206mp.com email domain and verified email'}
            </p>
          </div>
          <button
            id="streaming-toggle"
            onClick={handleStreamingToggle}
            disabled={!isAdmin}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              ${config.streamingEnabled ? 'bg-primary' : 'bg-muted'}
              ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${config.streamingEnabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {/* Status Indicator */}
        {config.streamingEnabled && isAdmin && (
          <div className="p-3 rounded-md">
            {isConnected ? (
              <div className="flex items-center gap-2 text-primary">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <span className="text-sm">Backend log streaming is active</span>
              </div>
            ) : connectionError ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-destructive">
                  <div className="w-2 h-2 bg-destructive rounded-full" />
                  <span className="text-sm">Connection error: {connectionError}</span>
                </div>
                <button
                  onClick={reconnect}
                  className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80"
                >
                  Retry Connection
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted">
                <div className="w-2 h-2 bg-muted rounded-full" />
                <span className="text-sm">Connecting...</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Console Controls Section */}
      <div className="bg-surface-2 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Console Controls</h2>

        <button
          onClick={handleClearConsole}
          className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/80 transition-colors"
        >
          Clear Console
        </button>
        <p className="text-sm text-muted mt-2">
          Clear all messages from the browser console
        </p>
      </div>

      {/* Current Configuration Display */}
      <div className="mt-8 p-4 bg-muted/20 rounded-md">
        <h3 className="text-sm font-semibold mb-2">Current Configuration</h3>
        <pre className="text-xs font-mono">
{JSON.stringify(config, null, 2)}
        </pre>
      </div>
    </div>
  );
}